import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { EditorialHeader } from '@/components/landing/Editorial'
import { HeroReveal, InteractiveDots, Pressable } from '@/components/motion'
import {
  RotatedProcessCards,
  ScrollCards,
  ServiceFaq,
  StatsRow,
  StickyFeatures,
  TeamDiagram,
} from '@/components/landing/service'
import { SolutionCta, SolutionStoryCard, splitLastWord, stepsLabel, type SolutionLayoutProps } from './shared'

/**
 * Strategy-insights layout (blue2purple layout 1), used by Sales: blue hero
 * with stats, pinned feature diagrams, rotated process cards, the blue
 * scroll-cards band, FAQ with a featured customer story, and the blue CTA band.
 */
export function InsightsLayout({ solution, story }: SolutionLayoutProps) {
  const Icon = solution.icon
  const [headLead, headLast] = splitLastWord(solution.featuresHeadline)

  return (
    <>
      {/* 1. Hero: full-bleed blue, team name in a pink box, two-line H1, stats */}
      <section className="relative overflow-hidden bg-[var(--color-landing-accent)] text-white">
        <InteractiveDots />
        <div className="landing-container relative z-10 pb-16 pt-16 md:pb-20 md:pt-24 lg:pb-24 lg:pt-28">
          <div className="lg:mx-auto lg:w-[86%]">
            <HeroReveal delay={0}>
              <p className="font-[family-name:var(--font-landing-display)] text-[clamp(1.5rem,2.6vw,2.75rem)] uppercase leading-[1.3]">
                <span className="inline-block -rotate-1 bg-[#ff00f2] px-[0.35em] py-[0.15em] text-white">
                  {solution.teamLabel}
                </span>
              </p>
            </HeroReveal>
            <HeroReveal delay={0.1}>
              <h1 className="mt-6 font-[family-name:var(--font-landing-display)] text-[clamp(2.25rem,5.6vw,6.25rem)] uppercase leading-[1.25] tracking-[-0.02em] text-white md:mt-8">
                <span className="block">{solution.headlineLines[0]}</span>{' '}
                <span className="block">{solution.headlineLines[1]}</span>
              </h1>
            </HeroReveal>
            <HeroReveal delay={0.2}>
              <p className="landing-text-body-lg mt-8 max-w-[58ch] text-pretty text-white/80 md:mt-10 md:text-xl">
                {solution.subheadline}
              </p>
            </HeroReveal>
            <HeroReveal delay={0.3} className="mt-10 flex flex-wrap gap-4">
              <Pressable className="inline-block">
                <Link
                  to="/contact"
                  className="landing-cta landing-cta--solid landing-cta--lg landing-cta--on-color landing-btn-sweep border-[#ff00f2] bg-[#ff00f2] text-2xl dark:border-[#ff00f2] dark:bg-[#ff00f2] dark:text-white lg:text-2xl"
                >
                  Book a demo
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Pressable>
              <a
                href="#how-it-works"
                className="landing-cta landing-cta--outline landing-cta--lg landing-cta--on-color landing-btn-sweep"
              >
                See how it works
              </a>
            </HeroReveal>

            <StatsRow stats={solution.heroStats} immediate className="mt-16 md:mt-24" />
          </div>
        </div>
      </section>

      {/* 2. Sticky features: pinned stage, diagrams open from the centre */}
      <section id="how-it-works" aria-labelledby="how-it-works-heading" className="scroll-mt-24 landing-section">
        <h2 id="how-it-works-heading" className="sr-only">
          {stepsLabel(solution)}
        </h2>
        <StickyFeatures
          accent="blue"
          steps={solution.steps.map((step, i) => ({
            title: step.title,
            description: step.description,
            visual: (active: boolean) => (
              <TeamDiagram teamName={solution.teamName} icon={Icon} nodes={step.nodes} layout={i} active={active} />
            ),
          }))}
        />
      </section>

      {/* 3. Rotated process cards: the four features */}
      <section className="landing-section overflow-x-clip">
        <div className="landing-container">
          <EditorialHeader
            heading={
              <>
                {headLead}{' '}
                <span className="landing-highlight">{headLast}</span>
              </>
            }
            lede={solution.featuresDescription}
            cta={{ label: 'Start free trial', href: '/register' }}
          />
          <RotatedProcessCards
            className="mt-16 lg:mt-6"
            cards={solution.features.map((f) => ({
              title: f.title,
              description: f.description,
              outcomes: f.outcomes,
            }))}
          />
        </div>
      </section>

      {/* 4. Scroll cards: the challenge, on blue */}
      <ScrollCards
        heading={solution.problemHeadline}
        body={solution.problemDescription}
        cta={{ label: 'Book a demo', href: '/contact' }}
        documents={solution.documents}
        cards={solution.problemPoints}
      />

      {/* 5. FAQ + featured customer story */}
      <ServiceFaq items={solution.faq} aside={<SolutionStoryCard solution={solution} story={story} />} />

      {/* 6. CTA band */}
      <SolutionCta solution={solution} tone="blue" />
    </>
  )
}

export default InsightsLayout
